/**
 * Driver Financial Privacy — reusable React context.
 *
 * Privacy-first earnings gate for the Driver Cockpit. Earnings are ALWAYS
 * hidden by default; revealing requires the driver's numeric PIN (when set)
 * else their account password (verified server-side by /driver/privacy/*).
 *
 * Display/privacy only — it NEVER changes how earnings are calculated. The
 * reveal state lives in browser memory with a ~60s inactivity auto-relock and
 * is dropped immediately when the driver leaves the Earnings view (callers use
 * hide()). No long-lived reveal token is stored.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { api } from "./api";

const MASK = "••••";
const RELOCK_MS = 60 * 1000; // 60s inactivity auto-relock (per spec)

const DriverFinancialPrivacyContext = createContext(null);

export const useDriverFinancialPrivacy = () => {
  const ctx = useContext(DriverFinancialPrivacyContext);
  if (!ctx) {
    return {
      hasPin: false, masked: true, revealed: false, loading: false, lockedUntil: null,
      unlock: async () => {}, hide: () => {}, refresh: async () => {},
      setPin: async () => {}, clearPin: async () => {}, mask: () => MASK,
    };
  }
  return ctx;
};

export const DriverFinancialPrivacyProvider = ({ children }) => {
  const [config, setConfig] = useState({ has_pin: false, locked_until: null });
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState(false);
  const relockTimer = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/driver/privacy/config");
      setConfig(data);
    } catch {
      setConfig({ has_pin: false, locked_until: null });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const clearRelock = () => {
    if (relockTimer.current) { clearTimeout(relockTimer.current); relockTimer.current = null; }
  };
  const armRelock = useCallback(() => {
    clearRelock();
    relockTimer.current = setTimeout(() => setRevealed(false), RELOCK_MS);
  }, []);

  // Reset the inactivity countdown on any user activity while revealed.
  useEffect(() => {
    if (!revealed) { clearRelock(); return; }
    armRelock();
    const bump = () => armRelock();
    const evts = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    evts.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    return () => {
      evts.forEach((e) => window.removeEventListener(e, bump));
      clearRelock();
    };
  }, [revealed, armRelock]);

  const unlock = useCallback(async (credential) => {
    await api.post("/driver/privacy/unlock", { credential });
    setRevealed(true);
    return true;
  }, []);

  const hide = useCallback(() => setRevealed(false), []);

  const setPin = useCallback(async (password, newPin) => {
    const { data } = await api.post("/driver/privacy/pin", { password, new_pin: newPin });
    setConfig((c) => ({ ...c, has_pin: true }));
    return data;
  }, []);

  const clearPin = useCallback(async (password) => {
    const { data } = await api.delete("/driver/privacy/pin", { data: { password } });
    setConfig((c) => ({ ...c, has_pin: false }));
    return data;
  }, []);

  const masked = !revealed;
  const mask = useCallback((value) => (masked ? MASK : value), [masked]);

  const value = {
    hasPin: !!config.has_pin,
    lockedUntil: config.locked_until || null,
    loading, revealed, masked, mask,
    unlock, hide, refresh, setPin, clearPin,
  };

  return (
    <DriverFinancialPrivacyContext.Provider value={value}>
      {children}
    </DriverFinancialPrivacyContext.Provider>
  );
};

export { MASK as DRIVER_MASK };
